import { useState } from 'react';
import { Filter, SortAsc, Bookmark, Sheet as SheetIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { SearchFilters } from '@/utils/optimizedQueries';
import { InstantSearchBar } from '@/components/InstantSearchBar';
import { FilterChips } from '@/components/FilterChips';

interface MobileStickyBarProps {
  filters: SearchFilters;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  onRemoveFilter: (filterKey: keyof SearchFilters) => void;
  onClearAll: () => void;
  activeFilters: Array<{
    key: keyof SearchFilters;
    label: string;
    value: any;
  }>;
  isLoading?: boolean;
  onSaveSearch?: () => void;
}

export const MobileStickyBar = ({
  filters,
  onFiltersChange,
  onRemoveFilter,
  onClearAll,
  activeFilters,
  isLoading,
  onSaveSearch
}: MobileStickyBarProps) => {
  const isMobile = useIsMobile();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  if (!isMobile) return null;

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'salary', label: 'Highest Salary' },
  ];

  const currentSort = sortOptions.find(opt => opt.value === (filters.sort_by || 'relevance'))?.label || 'Relevance';

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border z-50 safe-area-pb">
      <div className="flex items-center justify-around px-4 py-3">
        {/* Filters */}
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex flex-col items-center gap-1 h-12 min-w-[48px] relative"
            >
              <Filter className="h-4 w-4" />
              <span className="text-xs">Filters</span>
              {activeFilters.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilters.length}
                </div>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh] safe-area-pb">
            <SheetHeader>
              <SheetTitle>Filter Jobs</SheetTitle>
              <SheetDescription>
                Refine your job search with filters
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <InstantSearchBar
                filters={filters}
                onFiltersChange={onFiltersChange}
                isLoading={isLoading}
              />
              <FilterChips
                activeFilters={activeFilters}
                onRemoveFilter={onRemoveFilter}
                onClearAll={onClearAll}
              />
              <div className="flex gap-3">
                <Button 
                  onClick={() => setFilterSheetOpen(false)}
                  className="flex-1"
                >
                  Apply Filters
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    onClearAll();
                    setFilterSheetOpen(false);
                  }}
                  className="flex-1"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Sort */}
        <Sheet open={sortSheetOpen} onOpenChange={setSortSheetOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex flex-col items-center gap-1 h-12 min-w-[48px]"
            >
              <SortAsc className="h-4 w-4" />
              <span className="text-xs">Sort</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="safe-area-pb">
            <SheetHeader>
              <SheetTitle>Sort Jobs</SheetTitle>
              <SheetDescription>
                Currently sorted by: {currentSort}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-3">
              {sortOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={filters.sort_by === option.value ? "default" : "outline"}
                  className="w-full justify-start h-12"
                  onClick={() => {
                    onFiltersChange({ sort_by: option.value as SearchFilters['sort_by'] });
                    setSortSheetOpen(false);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Save Search */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex flex-col items-center gap-1 h-12 min-w-[48px]"
          onClick={onSaveSearch}
        >
          <Bookmark className="h-4 w-4" />
          <span className="text-xs">Save</span>
        </Button>
      </div>
    </div>
  );
};